import { Result, Subtask } from 'constants/interfaces'
import keys from 'constants/keys'
import { findScopedResult, isResultCompleted } from './results'

export const isExecutionComplete = isResultCompleted
export const findExecutionResult = findScopedResult
export const isMediaAnswer = (subtask: Subtask) =>
  [keys.SUBTASK_TYPES.IMAGE_SUBTASK, keys.SUBTASK_TYPES.VIDEO_SUBTASKS].includes(subtask.type)
export interface AnswerDraft {
  answer?: string
  votes?: number[]
}
export const draftFromResult = (result?: Result): AnswerDraft => ({
  answer: result?.answer == null ? '' : String(result.answer),
  votes: result?.poll_selections || [],
})
export const hasDraftAnswer = (subtask: Subtask, draft: AnswerDraft) => {
  switch (subtask.type) {
    case keys.SUBTASK_TYPES.POLL_SUBTASK:
    case keys.SUBTASK_TYPES.PRIVATE_POLL:
      return !!draft.votes?.length
    case keys.SUBTASK_TYPES.OPEN_QUESTION:
      return !!draft.answer?.trim() && (!subtask.is_numeric || Number.isFinite(Number(draft.answer)))
    default:
      return false
  }
}
/** Empty TODO rows are not submissions; completed/skipped acknowledgements and numeric zero are. */
export const hasExecutionResult = (subtask: Subtask, result?: Result): boolean =>
  isResultCompleted(result) ||
  hasDraftAnswer(subtask, draftFromResult(result)) ||
  !!(
    result?.files?.length ||
    result?.images?.length ||
    result?.videos?.length ||
    result?.audios?.length ||
    result?.external_link
  )

/** Compare only the selected answer type; response status and unrelated media are not editable drafts. */
export const hasAnswerChanges = (subtask: Subtask, draft: AnswerDraft, result?: Result): boolean => {
  const saved = draftFromResult(result)
  switch (subtask.type) {
    case keys.SUBTASK_TYPES.POLL_SUBTASK:
    case keys.SUBTASK_TYPES.PRIVATE_POLL: {
      const votes = draft.votes || []
      const savedVotes = saved.votes || []
      return votes.length !== savedVotes.length || votes.some((vote) => !savedVotes.includes(vote))
    }
    case keys.SUBTASK_TYPES.OPEN_QUESTION:
      return (draft.answer || '') !== (saved.answer || '')
    default:
      return false
  }
}

export const answerPayload = (subtask: Subtask, draft: AnswerDraft) => ({
  votes: draft.votes || [],
  answer: draft.answer?.trim() ? (subtask.is_numeric ? Number(draft.answer) : draft.answer) : null,
  isNumeric: subtask.is_numeric,
})
export const answerPreview = (subtask: Subtask, result?: Result): string => {
  if (!result) return ''
  if (subtask.type === keys.SUBTASK_TYPES.POLL_SUBTASK || subtask.type === keys.SUBTASK_TYPES.PRIVATE_POLL)
    return (result.poll_selections || [])
      .map((index) => subtask.options?.[index])
      .filter(Boolean)
      .join(', ')
  if (subtask.type === keys.SUBTASK_TYPES.OPEN_QUESTION) return result.answer == null ? '' : String(result.answer)
  return ''
}
export const nextUnanswered = (subtasks: Subtask[], current: string, completed: Set<string>) => {
  const index = subtasks.findIndex((subtask) => subtask.st_id === current)
  return (
    [...subtasks.slice(index + 1), ...subtasks.slice(0, index)].find((subtask) => !completed.has(subtask.st_id))
      ?.st_id || null
  )
}
